import React, { useEffect, useState } from "react";
import { Pagination } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAppliedCareer } from "store/slices";
import { limit } from "utils/constants";
import { getDataFromLocalStorage, objectToFormData } from "utils/helpers";
import Button from "components/form/Button";
import Card from "components/Layout/Card";
import Loader from "components/Layout/Loader";

const CareerSupport = () => {
  const navigate = useNavigate()
  const { userId } = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [jobList, setJobList] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    offset: 0,
    limit: limit,
  });

  const totalPage = Math.ceil(pagination.total / pagination.limit) || 1;
  const activePage = Math.floor(pagination.offset / pagination.limit) + 1;

  // Fetch applied jobs
  const getAppliedJobData = async (params) => {
    setIsLoading(true);
    try {
      const formData = objectToFormData(params);
      const response = await dispatch(fetchAppliedCareer(formData));

      const jobData = response?.data?.career_details || [];
      setJobList(jobData);
      setSelectedJob(jobData.length ? jobData[0] : null);

      setPagination((prev) => ({
        ...prev,
        total: response?.data?.result_count || 0,
        offset: params.offset || 0,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!['0', '1', '6'].includes(getDataFromLocalStorage('user_type'))) {
      navigate('/');
    }

    getAppliedJobData({ ...pagination, user_id: userId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handlePagination = (newOffset) => {
    getAppliedJobData({ ...pagination, offset: newOffset, user_id: userId });
  };

  // ✅ CASE 1: Show loader
  if (isLoading) {
    return <Card className="d-flex justify-content-center align-items-center cpt-60 cpb-60 cmt-10">
      <Loader size="sm" />
    </Card>;
  }

  // ✅ CASE 2: No data found — show only one centered card
  if (!isLoading && jobList.length === 0) {
    return (
      <Card className="d-flex justify-content-center align-items-center cpt-60 cpb-60 cmt-10">
        <div className="text-16-500 color-davys-gray">No applied jobs found.</div>
      </Card>
    );
  }


  // ✅ CASE 3: Data available
  return (
    <div className="cpt-12">
      <div className="row">
        {/* Left side (Job List) */}
        <div className="col-12 col-md-8">
          <div className="row g-3">
            {jobList.map((job) => (
              <div className="col-12 col-sm-6 col-md-6 " key={job.id}>
                <div
                  className={`br-10 p-3 h-100 pointer transition-all  ${selectedJob?.id === job.id
                    ? "border-primary bg-f4ff"
                    : "border border-light bg-white"
                    }`}
                  style={{
                    border:
                      selectedJob?.id === job.id
                        ? "1px solid #4A6CF7"
                        : "1px solid #E5E5E5",
                  }}
                  onClick={() => setSelectedJob(job)}
                >
                  <div
                    className={`text-14-500-24 ${selectedJob?.id === job.id ? "color-new-car" : "color-1717"
                      }`}
                  >
                    {job?.career_name || "-"}
                  </div>
                  <div
                    className={`text-14-300-24 cmt-5 two-line-elipses ${selectedJob?.id === job.id ? "color-new-car" : "color-6866"
                      }`}
                  >
                    {job?.career_description || "-"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right side (Job Details) */}
        <div className="col-12 col-md-4">
          {selectedJob && (
            <div className="br-10 border bg-white p-3">
              <div className="text-16-500-20 color-new-car">
                {selectedJob?.career_name || "-"}
              </div>
              <div className="text-14-400-24 color-black-olive cmt-8">
                {selectedJob?.career_description || "-"}
              </div>

              <div className="cmt-10">
                <div className="text-14-400-18 color-black-olive">
                  <span>Applied on: </span>
                  {selectedJob?.created_at || "-"}
                </div>
                <div className="text-14-400-18 color-black-olive cmt-16">
                  <span className="text-13-500-16">Status: </span>
                  <span
                    className={
                      selectedJob.status === "0"
                        ? "color-C991"
                        : selectedJob.status === "1"
                          ? "color-success"
                          : "color-5110"
                    }
                  >
                    {selectedJob.status === "0"
                      ? "Pending"
                      : selectedJob.status === "1"
                        ? "Approved"
                        : "-"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPage > 1 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap footer-block cmt-30 cpb-30">
          <div className="d-flex gap-4">
            <Button
              icon={<i className="bi bi-chevron-left me-1" />}
              text="Previous"
              btnStyle={activePage === 1 ? "primary-gray" : "primary-dark"}
              className="cps-20 cpe-20"
              onClick={() => {
                if (activePage !== 1) {
                  handlePagination(pagination.offset - pagination.limit);
                }
              }}
              isRounded
            />
            <Button
              rightIcon={<i className="bi bi-chevron-right ms-2" />}
              text="Next"
              btnStyle={
                activePage === totalPage ? "primary-gray" : "primary-dark"
              }
              className="cps-40 cpe-30"
              onClick={() => {
                if (activePage !== totalPage) {
                  handlePagination(pagination.offset + pagination.limit);
                }
              }}
              isRounded
            />
          </div>

          <Pagination className="d-flex align-items-center unset-m">
            <div className="text-16-400 color-davys-gray me-1">Page</div>
            <Pagination.Item disabled>{activePage}</Pagination.Item>
            <div className="text-16-400 color-davys-gray ms-1 me-2">of</div>
            <Pagination.Item disabled>{totalPage}</Pagination.Item>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default CareerSupport;
